import type {ApprovalView} from '@src/api/approval/api.d';
import type {FieldView} from '@src/api/field/api.d';
import type {DocumentView} from '@src/api/types/document';
import type {CheckboxGroupsMap} from './getCheckboxGroupStatuses';

import {APPROVAL_TYPES, FIELD_TYPES} from '@api/constants';
import {
    getAreConstrainedCheckboxGroupsSatisfied, getCheckboxGroupsRequiringAction, getIsFieldInConstrainedCheckboxGroup
} from './getCheckboxGroupStatuses';
import {getIsFieldFilled} from './getIsFieldFilled';
import {getHasFieldValidationError} from './getFieldValidationErrorKey';

export interface DocumentStatuses {
    isConfirmed: boolean;
    isReadyToConfirm: boolean;
    isReview: boolean;
    isPending: boolean;
    isOngoing: boolean;
    isToDo: boolean;
    isLast: boolean;
    isAcceptOnly: boolean;
    isEligibleToSign: boolean;
}

export interface DocumentWithStatuses extends DocumentView {
    isConfirmed: boolean;
    isReadyToConfirm: boolean;
    isReview: boolean;
    isPending: boolean;
    isOngoing: boolean;
    isToDo: boolean;
    isLast: boolean;
    isAcceptOnly: boolean;
    isEligibleToSign: boolean;
}

interface GetDocumentStatusesParams {
    document: DocumentView;
    approvals: ApprovalView[];
    fields: FieldView[];
    allDocuments: DocumentView[];
    approvalsMap?: Record<string, ApprovalView[]>;
    fieldsMap?: Record<string, FieldView[]>;
}

interface RequiredItemsCompletionStatus {
    isAllApprovalsSigned: boolean;
    isAllApprovalsConfirmed: boolean;
    isAllFieldsFilled: boolean;
    hasAnyRequiredActions: boolean;
}

function getIsAcceptApproval(approval: ApprovalView): boolean {
    return approval.signing_type?.toLowerCase() === APPROVAL_TYPES.ACCEPT;
}

function getSignableFields(fields: FieldView[], approvals: ApprovalView[]): FieldView[] {
    const acceptApprovalIds = new Set(
        approvals.filter(getIsAcceptApproval).map((a) => a.id)
    );
    return fields.filter((field) => !acceptApprovalIds.has(field.approval_id));
}

function getIsAcceptOnlyDocument(approvals: ApprovalView[]): boolean {
    return approvals.some(getIsAcceptApproval);
}

function getRequiredApprovals(approvals: ApprovalView[]): ApprovalView[] {
    return approvals.filter((approval) => !approval.is_optional);
}

function getRequiredFields(fields: FieldView[]): FieldView[] {
    return fields.filter((field) => {
        const isLabel = field.type?.toLowerCase() === FIELD_TYPES.LABEL;
        const isAutoField = Boolean(field.is_auto_field);

        return field.is_required && !isLabel && !isAutoField;
    });
}

function getRadioGroupKey(field: FieldView): string {
    return `${field.approval_id}::${field.group}`;
}

function getRequiredRadioGroups(requiredFields: FieldView[]): Set<string> {
    const groups = new Set<string>();

    for (const field of requiredFields) {
        const isRadio = field.type?.toLowerCase() === FIELD_TYPES.RADIO;

        if (isRadio && field.group) {
            groups.add(getRadioGroupKey(field));
        }
    }

    return groups;
}

function getIsRadioGroupFilled(allFields: FieldView[], groupKey: string): boolean {
    return allFields.some((field) => {
        const isRadio = field.type?.toLowerCase() === FIELD_TYPES.RADIO;
        return isRadio && field.group && getRadioGroupKey(field) === groupKey && getIsFieldFilled(field);
    });
}

/**
 * A required radio field is tallied through its group, and a checkbox field whose
 * group already demands action is tallied through that group, so both must be
 * excluded here to avoid double-counting.
 */
function getIsFieldCountedIndividually(field: FieldView, groupsRequiringAction: CheckboxGroupsMap): boolean {
    const isRadio = field.type?.toLowerCase() === FIELD_TYPES.RADIO;
    const isRadioInGroup = isRadio && Boolean(field.group);
    const isInCheckboxGroupRequiringAction = getIsFieldInConstrainedCheckboxGroup(field, groupsRequiringAction);

    return !isRadioInGroup && !isInCheckboxGroupRequiringAction;
}

function getRequiredFieldsCountForActions(
    requiredFields: FieldView[],
    groupsRequiringAction: CheckboxGroupsMap
): number {
    const requiredRadioGroups = getRequiredRadioGroups(requiredFields);
    const individuallyCountedFieldsCount = requiredFields.filter(
        (field) => getIsFieldCountedIndividually(field, groupsRequiringAction)
    ).length;

    return individuallyCountedFieldsCount + requiredRadioGroups.size + groupsRequiringAction.size;
}

function getFilledRequiredFieldsCount(
    requiredFields: FieldView[],
    allFields: FieldView[],
    groupsRequiringAction: CheckboxGroupsMap
): number {
    const requiredRadioGroups = getRequiredRadioGroups(requiredFields);
    let count = 0;

    for (const field of requiredFields) {
        if (getIsFieldCountedIndividually(field, groupsRequiringAction) && getIsFieldFilled(field)) {
            count += 1;
        }
    }

    for (const groupKey of requiredRadioGroups) {
        if (getIsRadioGroupFilled(allFields, groupKey)) {
            count += 1;
        }
    }

    groupsRequiringAction.forEach((group) => {
        if (group.isSatisfied) {
            count += 1;
        }
    });

    return count;
}

function getIsAllRequiredFieldsFilled(
    requiredFields: FieldView[],
    allFields: FieldView[],
    groupsRequiringAction: CheckboxGroupsMap
): boolean {
    for (const field of requiredFields) {
        if (getIsFieldCountedIndividually(field, groupsRequiringAction) && !getIsFieldFilled(field)) {
            return false;
        }
    }

    const requiredRadioGroups = getRequiredRadioGroups(requiredFields);

    for (const groupKey of requiredRadioGroups) {
        if (!getIsRadioGroupFilled(allFields, groupKey)) {
            return false;
        }
    }

    return getAreConstrainedCheckboxGroupsSatisfied(groupsRequiringAction);
}

function getIsAllApprovalsConfirmed(approvals: ApprovalView[]): boolean {
    return approvals.length > 0 && approvals.every((approval) => Boolean(approval.confirmed_at));
}

function getIsAllRequiredItemsConfirmed(approvals: ApprovalView[], fields: FieldView[]): boolean {
    const requiredApprovals = getRequiredApprovals(approvals);
    const requiredFields = getRequiredFields(fields);
    const groupsRequiringAction = getCheckboxGroupsRequiringAction(fields);

    const hasRequiredActions = requiredApprovals.length > 0
        || requiredFields.length > 0
        || groupsRequiringAction.size > 0;

    if (!hasRequiredActions) {
        return false;
    }

    const isAllRequiredApprovalsConfirmed = requiredApprovals.every(
        (approval) => Boolean(approval.confirmed_at)
    );

    return isAllRequiredApprovalsConfirmed
        && getIsAllRequiredFieldsFilled(requiredFields, fields, groupsRequiringAction);
}

function getRequiredItemsCompletionStatus(
    approvals: ApprovalView[],
    fields: FieldView[]
): RequiredItemsCompletionStatus {
    const requiredApprovals = getRequiredApprovals(approvals);
    const requiredFields = getRequiredFields(fields);
    const groupsRequiringAction = getCheckboxGroupsRequiringAction(fields);

    return {
        isAllApprovalsSigned: requiredApprovals.every((approval) => Boolean(approval.signed_at)),
        isAllApprovalsConfirmed: requiredApprovals.every((approval) => Boolean(approval.confirmed_at)),
        isAllFieldsFilled: getIsAllRequiredFieldsFilled(requiredFields, fields, groupsRequiringAction),
        hasAnyRequiredActions: requiredApprovals.length > 0 ||
            requiredFields.length > 0 ||
            groupsRequiringAction.size > 0
    };
}

function getHasAnyProgress(approvals: ApprovalView[], fields: FieldView[]): boolean {
    const hasAnySignedApproval = approvals.some((approval) => Boolean(approval.signed_at));
    const hasAnyFilledField = fields.some(getIsFieldFilled);

    return hasAnySignedApproval || hasAnyFilledField;
}

function getIsLastDocument(document: DocumentView, allDocuments: DocumentView[]): boolean {
    if (allDocuments.length === 0) {
        return false;
    }

    const maxOrder = Math.max(...allDocuments.map((doc) => doc.order || 0));

    return document?.order === maxOrder;
}

function getHasPendingPreviousDocuments(
    document: DocumentView,
    allDocuments: DocumentView[],
    approvalsMap: Record<string, ApprovalView[]>,
    fieldsMap: Record<string, FieldView[]> = {}
): boolean {
    const currentOrder = document.order || 0;
    const previousDocuments = allDocuments.filter((doc) => (doc.order || 0) < currentOrder);

    return previousDocuments.some((prevDoc) => {
        const docApprovals = approvalsMap[prevDoc.id] || [];
        const docFields = fieldsMap[prevDoc.id] || [];
        const isReview = docApprovals.length === 0;

        if (isReview) {
            return false;
        }

        const isConfirmed = getIsAllApprovalsConfirmed(docApprovals);

        return !isConfirmed;
    });
}

/**
 * Calculates all document statuses based on approvals and fields state.
 *
 * Status definitions:
 * - isConfirmed: All approvals have confirmed_at (document was successfully confirmed)
 * - isReadyToConfirm: All required approvals signed AND all required fields filled (but not yet confirmed)
 * - isReview: Document has no approvals (view-only document)
 * - isPending: Document requires action (!isConfirmed && !isReview)
 * - isOngoing: Document has partial progress (some signatures but not all required completed)
 * - isToDo: Document has not been started (no signatures yet, not review, not confirmed)
 * - isLast: Document is the last one in the sequence by order
 * - isAcceptOnly: Document has an approval with signing_type "accept" (acceptance without signature)
 * - isEligibleToSign: Document can be signed (not confirmed and no pending previous documents)
 */
export function getDocumentStatuses({
    document,
    approvals,
    fields,
    allDocuments,
    approvalsMap = {},
    fieldsMap = {}
}: GetDocumentStatusesParams): DocumentStatuses {
    const completionStatus = getRequiredItemsCompletionStatus(approvals, fields);
    const hasAnyProgress = getHasAnyProgress(approvals, fields);
    const isReview = approvals.length === 0;

    const isConfirmed = getIsAllApprovalsConfirmed(approvals);

    const isReadyToConfirm = completionStatus.hasAnyRequiredActions &&
        completionStatus.isAllApprovalsSigned &&
        completionStatus.isAllFieldsFilled &&
        !isConfirmed;

    const isPending = !isConfirmed && !isReview;
    const isOngoing = isPending && hasAnyProgress && !isReadyToConfirm;
    const isToDo = isPending && !hasAnyProgress;
    const isLast = getIsLastDocument(document, allDocuments);
    const isAcceptOnly = getIsAcceptOnlyDocument(approvals);

    const hasPendingPreviousDocuments = getHasPendingPreviousDocuments(
        document,
        allDocuments,
        approvalsMap,
        fieldsMap
    );
    const isEligibleToSign = !isConfirmed && !hasPendingPreviousDocuments;

    return {
        isConfirmed,
        isReadyToConfirm,
        isReview,
        isPending,
        isOngoing,
        isToDo,
        isLast,
        isAcceptOnly,
        isEligibleToSign
    };
}

/**
 * Enriches a document with all computed statuses.
 */
export function getDocumentWithStatuses({
    document,
    approvals,
    fields,
    allDocuments,
    approvalsMap = {},
    fieldsMap = {}
}: GetDocumentStatusesParams): DocumentWithStatuses {
    const statuses = getDocumentStatuses({
        document,
        approvals,
        fields,
        allDocuments,
        approvalsMap,
        fieldsMap
    });

    return {
        ...document,
        ...statuses
    };
}

/**
 * Checks if all documents in the array are either confirmed or review-only.
 */
export function getAreAllDocumentsConfirmed(documents: DocumentWithStatuses[]): boolean {
    if (!documents || documents.length === 0) {
        return false;
    }

    return documents.every((doc) => doc.isConfirmed || doc.isReview);
}

/**
 * Gets the ID of the next pending document that requires action.
 * Documents are evaluated in order (by `order` field).
 * Review documents are skipped as they don't require action.
 */
export function getNextPendingDocumentId(
    documents: DocumentWithStatuses[],
    currentDocumentId?: string | null
): string | null {
    if (!documents || documents.length === 0) {
        return null;
    }

    const sortedDocuments = [...documents].sort((a, b) => (a.order || 0) - (b.order || 0));

    const currentDocumentOrder = currentDocumentId
        ? sortedDocuments.find((doc) => doc.id === currentDocumentId)?.order ?? -1
        : -1;

    const nextPendingAfterCurrent = sortedDocuments.find((doc) => {
        const documentOrder = doc.order || 0;
        const isAfterCurrentDocument = !currentDocumentId || documentOrder > currentDocumentOrder;

        return isAfterCurrentDocument && doc.isPending;
    });

    if (nextPendingAfterCurrent) {
        return nextPendingAfterCurrent.id;
    }

    const firstPendingDocument = sortedDocuments.find((doc) => doc.isPending);

    return firstPendingDocument?.id ?? null;
}

/**
 * Gets the ID of the next document the signer should see in the ceremony.
 * Unlike getNextPendingDocumentId, review-only documents are NOT skipped:
 * the signer must be able to read documents that require no signature (BO-777).
 * Walks documents in order after the current one and returns the first that is
 * pending or review-only. Falls back to the first pending document overall
 * (an earlier document may still require action), or null when there is
 * nothing left to show.
 */
export function getNextCeremonyDocumentId(
    documents: DocumentWithStatuses[],
    currentDocumentId?: string | null
): string | null {
    if (!documents || documents.length === 0) {
        return null;
    }

    const sortedDocuments = [...documents].sort((a, b) => (a.order || 0) - (b.order || 0));

    const currentDocumentOrder = currentDocumentId
        ? sortedDocuments.find((doc) => doc.id === currentDocumentId)?.order ?? -1
        : -1;

    const nextDocumentAfterCurrent = sortedDocuments.find((doc) => {
        const documentOrder = doc.order || 0;
        const isAfterCurrentDocument = !currentDocumentId || documentOrder > currentDocumentOrder;

        return isAfterCurrentDocument && (doc.isPending || doc.isReview);
    });

    if (nextDocumentAfterCurrent) {
        return nextDocumentAfterCurrent.id;
    }

    const firstPendingDocument = sortedDocuments.find(
        (doc) => doc.id !== currentDocumentId && doc.isPending
    );

    return firstPendingDocument?.id ?? null;
}

/**
 * Gets the ID of the first document by order, regardless of its status.
 * Used for the initial ceremony redirect so the signer always lands on the
 * first document in the list, even if it is review-only (BO-703).
 */
export function getFirstDocumentId(documents: DocumentWithStatuses[]): string | null {
    if (!documents || documents.length === 0) {
        return null;
    }

    const sortedDocuments = [...documents].sort((a, b) => (a.order || 0) - (b.order || 0));

    return sortedDocuments[0]?.id ?? null;
}

/**
 * Checks if it's the signer's turn to sign a specific document.
 * A document can be signed only if all previous documents (by order) are confirmed or review-only.
 * @deprecated Use isEligibleToSign from DocumentStatuses instead
 */
export function getCanSignDocument(
    documents: DocumentWithStatuses[],
    documentId: string
): boolean {
    const targetDocument = documents.find((doc) => doc.id === documentId);

    if (!targetDocument || targetDocument.isConfirmed) {
        return false;
    }

    const targetDocumentOrder = targetDocument.order || 0;
    const hasPendingPreviousDocuments = documents.some((doc) => {
        const documentOrder = doc.order || 0;

        return documentOrder < targetDocumentOrder && doc.isPending;
    });

    return !hasPendingPreviousDocuments;
}

/**
 * Checks if all required approvals for a document are signed (but may not be confirmed yet).
 */
export function getIsAllRequiredActionsDone(approvals: ApprovalView[], fields: FieldView[]): boolean {
    const completionStatus = getRequiredItemsCompletionStatus(approvals, fields);

    return completionStatus.isAllApprovalsSigned
        && completionStatus.isAllFieldsFilled
        && !fields.some(getHasFieldValidationError);
}

/**
 * @deprecated Use getIsAllRequiredActionsDone instead
 */
export const getIsRequiredActionsDone = getIsAllRequiredActionsDone;

export const getHasInvalidFieldValue = (fields: FieldView[]): boolean => fields.some(getHasFieldValidationError);

/**
 * Checks if document has only optional actions (no required approvals or fields).
 */
export function getHasOnlyOptionalActions(approvals: ApprovalView[], fields: FieldView[]): boolean {
    const hasApprovals = approvals.length > 0;
    const requiredApprovals = getRequiredApprovals(approvals);
    const requiredFields = getRequiredFields(fields);
    const groupsRequiringAction = getCheckboxGroupsRequiringAction(fields);

    return hasApprovals &&
        requiredApprovals.length === 0 &&
        requiredFields.length === 0 &&
        groupsRequiringAction.size === 0;
}

export interface ActionCounts {
    requiredActionsTotalCount: number;
    requiredCompletedActionsCount: number;
    optionalActionsTotalCount: number;
    optionalCompletedActionsCount: number;
    optionalApprovalsCount: number;
    optionalCompletedApprovalsCount: number;
    allActionsTotalCount: number;
    allCompletedActionsCount: number;
}

function getOptionalApprovals(approvals: ApprovalView[]): ApprovalView[] {
    return approvals.filter((approval) => approval.is_optional);
}

function getOptionalFields(fields: FieldView[], groupsRequiringAction: CheckboxGroupsMap): FieldView[] {
    return fields.filter((field) => {
        const isInCheckboxGroupRequiringAction = getIsFieldInConstrainedCheckboxGroup(field, groupsRequiringAction);

        return !field.is_required && !isInCheckboxGroupRequiringAction;
    });
}

interface GetActionCountsParams {
    approvals: ApprovalView[];
    fields: FieldView[];
}

/**
 * Calculates counts of required and optional actions (approvals and fields).
 * Use this function when you need numeric counts for progress indicators.
 */
export function getActionCounts({approvals, fields}: GetActionCountsParams): ActionCounts {
    const filteredFields = getSignableFields(fields, approvals);
    const groupsRequiringAction = getCheckboxGroupsRequiringAction(filteredFields);

    const requiredApprovals = getRequiredApprovals(approvals);
    const optionalApprovals = getOptionalApprovals(approvals);
    const requiredFields = getRequiredFields(filteredFields);
    const optionalFields = getOptionalFields(filteredFields, groupsRequiringAction);

    const requiredApprovalsCount = requiredApprovals.length;
    const optionalApprovalsCount = optionalApprovals.length;
    const requiredFieldsCount = getRequiredFieldsCountForActions(requiredFields, groupsRequiringAction);
    const optionalFieldsCount = optionalFields.length;

    const acceptedRequiredApprovalsCount = requiredApprovals.filter(
        (approval) => Boolean(approval.signed_at)
    ).length;
    const acceptedOptionalApprovalsCount = optionalApprovals.filter(
        (approval) => Boolean(approval.signed_at)
    ).length;
    const filledRequiredFieldsCount = getFilledRequiredFieldsCount(requiredFields, filteredFields, groupsRequiringAction);
    const filledOptionalFieldsCount = optionalFields.filter(getIsFieldFilled).length;

    const requiredActionsTotalCount = requiredApprovalsCount + requiredFieldsCount;
    const requiredCompletedActionsCount = acceptedRequiredApprovalsCount + filledRequiredFieldsCount;
    const optionalActionsTotalCount = optionalApprovalsCount + optionalFieldsCount;
    const optionalCompletedActionsCount = acceptedOptionalApprovalsCount + filledOptionalFieldsCount;
    const allActionsTotalCount = requiredActionsTotalCount + optionalActionsTotalCount;
    const allCompletedActionsCount = requiredCompletedActionsCount + optionalCompletedActionsCount;

    return {
        requiredActionsTotalCount,
        requiredCompletedActionsCount,
        optionalActionsTotalCount,
        optionalCompletedActionsCount,
        optionalApprovalsCount,
        optionalCompletedApprovalsCount: acceptedOptionalApprovalsCount,
        allActionsTotalCount,
        allCompletedActionsCount
    };
}

export {getIsAcceptApproval, getSignableFields, getIsAcceptOnlyDocument};
